import { Box, CircularProgress, DialogContent, Modal } from "@mui/material"
import React, { useCallback, useEffect } from "react"
import QrCode from "react-qr-code"
import { usePageviewTracking } from "src/screens/Detail/use-pageview-tracking"
import { theme, TI } from "src/utilities/theme"

import { DGText } from "../../../components/common/DGText"
import { useModalDismiss } from "../../../hooks/use-modal-dismiss"
import useTranslate from "../../../hooks/use-translate"
import { useUserSubscriptionStatus } from "../../../hooks/use-user-subscription-status"
import { s } from "../../../utilities/common"
import { usePauseFocus } from "../../../utilities/use-pause-focus"

export const SubscribeModal = ({
  isOpen,
  close,
  onSuccess,
}: {
  isOpen: boolean
  close: () => void
  onSuccess: () => void
}) => {
  return isOpen ? (
    <>
      <Screen isOpen={isOpen} close={close} onSuccess={onSuccess} />
    </>
  ) : (
    <></>
  )
}

const Screen = ({
  isOpen,
  close,
  onSuccess,
}: {
  isOpen: boolean
  close: () => void
  onSuccess: () => void
}) => {
  const t = useTranslate()

  const trackPageview = usePageviewTracking()

  useEffect(() => {
    if (isOpen) {
      trackPageview("subscribe", "account/subscribe")
    }
  }, [isOpen])

  useModalDismiss(close)
  const { refreshCurrentUserAndGetSubscriptionState } =
    useUserSubscriptionStatus()

  const ariaLabel = `${t("smarttv__subscribe_title")}: ${t("smarttv__subscribe_description")}: ${t("smarttv__subscribe_polling_info")}`

  const poll = useCallback(
    () =>
      setInterval(async () => {
        if (await refreshCurrentUserAndGetSubscriptionState()) {
          onSuccess()
        }
      }, 5_000),
    [],
  )

  useEffect(() => {
    const timer = poll()
    return () => {
      if (timer) clearInterval(timer)
    }
  }, [])

  const FocusTrap = usePauseFocus(ariaLabel)

  return (
    <Modal
      hideBackdrop
      open={isOpen}
      sx={{
        height: "100vh",
        width: "100vw",
        backgroundColor: theme.palette.primary.main,
        "& > *": { transition: "none !important" },
      }} // don't flash the screen }}
    >
      <DialogContent>
        <FocusTrap />
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            alignItems: "center",
            height: "100%",
            color: theme.palette.white.main,
            textAlign: "center",
          }}
        >
          <DGText tx={TI.H03tv} sx={{ width: s("950px") }}>
            {t("smarttv__subscribe_title")}
          </DGText>
          <DGText tx={TI.Subheadline01tv} sx={{ mt: s("72px"), mb: s("33px") }}>
            {t("smarttv__subscribe_description")}
          </DGText>
          <QrCode
            size={s(260)}
            style={{
              width: s("261px"),
              height: s("260px"),
              paddingTop: s("24px"),
              paddingBottom: s("24px"),
              paddingLeft: s("28px"),
              paddingRight: s("28px"),
              backgroundColor: theme.palette.secondary.main,
            }}
            bgColor={theme.palette.secondary.main}
            fgColor={"white"}
            viewBox={`0 0 260 260`}
            value={"https://www.stage-plus.com/tickets"}
          />
          <DGText tx={TI.H05tv} sx={{ mt: s("15px") }}>
            stage-plus.com/tickets
          </DGText>
          <DGText tx={TI.Subheadline01tv} sx={{ mt: s("72px"), mb: s("33px") }}>
            {t("smarttv__subscribe_polling_info")}{" "}
            <CircularProgress
              sx={{ color: theme.palette.white.main }}
              size={20}
            />
          </DGText>
        </Box>
      </DialogContent>
    </Modal>
  )
}
